// @ts-strict-ignore
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import {
  getImagePreview,
  MEDIA_VIEWER_UNZOOMED_SIZES,
} from '@fc-shared/ui/media-view-dialog/media-view.service';
import { buildThumbnailSrcset } from '@fc-shared/ui/image-loader/thumbnail-tiers';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'fc-media-view-photo',
  template: `
    <img
      #imgEl
      class="current-media"
      alt="file-image"
      [hidden]="!isFileLoaded()"
      [src]="fileImage()"
      [attr.srcset]="srcset()"
      [attr.sizes]="sizes()"
      [style.transform]="transform()"
      [style.cursor]="cursor()"
      [style.transition]="isDragging() ? 'none' : 'transform 160ms ease'"
      [style.userSelect]="'none'"
      [attr.draggable]="false"
      (load)="onImageLoad()"
      (click)="onImageClick($event)"
      (dragstart)="$event.preventDefault()"
      (pointerdown)="onPointerDown($event)"
      (pointermove)="onPointerMove($event)"
      (pointerup)="onPointerUp($event)"
      (pointercancel)="onPointerUp($event)"
      (pointerleave)="onPointerUp($event)"
    />

    @if (loading()) {
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <mat-spinner [diameter]="40"></mat-spinner>
      </div>
    }
  `,
  styles: [
    `
      @use 'var' as *;

      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: relative;
      }

      .current-media {
        touch-action: none;
        -webkit-user-drag: none;
        max-width: 100%;
        max-height: 100%;
        @media (max-width: $sm-size) {
          width: 100%;
        }
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatProgressSpinner],
})
export class MediaViewPhotoComponent {
  @ViewChild('imgEl', { static: false }) imageRef: ElementRef<HTMLImageElement>;
  file = input.required<AttachmentModel>();
  isFileLoaded = input<boolean>(true);
  fileLoaded = output<boolean>();
  fileImage = signal<string>(null);
  loading = signal(false);
  // Tiers only, never the original: the xxl tier already covers the zoom.
  srcset = computed(
    () => buildThumbnailSrcset(this.file()?.thumbnails).join(', ') || null,
  );
  // Widening `sizes` while zoomed makes the browser upgrade to a larger tier in
  // place (it never downgrades), so zooming needs no src swap and no reflash.
  sizes = computed(() =>
    this.isZoomed() ? `${100 * this.zoomScale}vw` : MEDIA_VIEWER_UNZOOMED_SIZES,
  );

  // Zoom and pan state
  isZoomed = signal(false);
  isDragging = signal(false);
  private scale = 1;
  private readonly zoomScale = 2.5;
  private translateX = 0;
  private translateY = 0;
  private dragStartX = 0;
  private dragStartY = 0;
  private lastTranslateX = 0;
  private lastTranslateY = 0;
  private pointerDownX = 0;
  private pointerDownY = 0;
  private movedDuringDrag = false;
  private suppressClickUntil = 0;

  constructor() {
    effect(() => {
      this.loading.set(true);
      this.fileImage.set(getImagePreview(this.file()));
    });
  }

  transform(): string {
    return `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
  }

  cursor(): string {
    if (!this.isZoomed()) return 'zoom-in';
    return this.isDragging() ? 'grabbing' : 'grab';
  }

  onImageClick(event: MouseEvent): void {
    // Prevent parent from toggling controls
    event.stopPropagation();
    if (Date.now() < this.suppressClickUntil) return;
    if (this.isZoomed()) {
      this.resetZoom();
    } else {
      this.applyZoom();
    }
  }

  onImageLoad(): void {
    this.loading.set(false);
    this.fileLoaded.emit(true);
  }

  private applyZoom(): void {
    this.isZoomed.set(true);
    this.scale = this.zoomScale;
  }

  private resetZoom(): void {
    this.isZoomed.set(false);
    this.isDragging.set(false);
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.isZoomed()) return;
    event.stopPropagation();
    event.preventDefault();
    const target = this.imageRef?.nativeElement;
    if (target && target.setPointerCapture) {
      target.setPointerCapture(event.pointerId);
    }
    this.isDragging.set(true);
    this.movedDuringDrag = false;
    this.dragStartX = event.clientX - this.lastTranslateX;
    this.dragStartY = event.clientY - this.lastTranslateY;
    this.pointerDownX = event.clientX;
    this.pointerDownY = event.clientY;
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging()) return;
    event.preventDefault();
    event.stopPropagation();
    const nextX = event.clientX - this.dragStartX;
    const nextY = event.clientY - this.dragStartY;

    // mark as drag if moved beyond small threshold
    if (!this.movedDuringDrag) {
      const dx = event.clientX - this.pointerDownX;
      const dy = event.clientY - this.pointerDownY;
      if (Math.hypot(dx, dy) > 3) this.movedDuringDrag = true;
    }

    this.translateX = nextX;
    this.translateY = nextY;
    this.lastTranslateX = this.translateX;
    this.lastTranslateY = this.translateY;
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isDragging()) return;
    event.stopPropagation();
    this.isDragging.set(false);
    if (this.movedDuringDrag) {
      // prevent the click event right after dragging
      this.suppressClickUntil = Date.now() + 250;
    }
  }
}
